<script lang="ts">
  import { fetchPostThread, queueReply, setMemberContact } from '@api';
  import { currentPath, navigate } from '../lib/router.svelte';
  import { askHelperToSend } from '../lib/helper.svelte';
  import SkoolLink from '@ui/SkoolLink.svelte';
  import { skoolProfileUrl } from '@ui/member';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import StatCard from '../lib/ui/StatCard.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import StageBadge from '../lib/ui/StageBadge.svelte';
  import PlanCell from '@ui/PlanCell.svelte';
  import RenewalCell from '@ui/RenewalCell.svelte';
  import ContributorBadge from '../lib/ui/ContributorBadge.svelte';
  import StyleBadge from '../lib/ui/StyleBadge.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import RoleJourney from '@ui/RoleJourney.svelte';
  import MemberActivity from '../lib/ui/MemberActivity.svelte';
  import ThreadDrawer from '../lib/ui/ThreadDrawer.svelte';
  import TrendChart from '../lib/charts/TrendChart.svelte';
  import Heatmap from '@ui/Heatmap.svelte';
  import Clock from '../lib/charts/Clock.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    memberStatusMeta,
    type AdminUser,
    type MemberAnalytics,
    type ReplyPartner,
    type PostDetail,
  } from '../lib/admin';
  import { fetchMemberAnalytics } from '@api';
  import { windowLabel } from '../lib/periods';
  import { attributionLabel, fmtDate } from '../lib/revenue';
  import { community, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { type CommentRow } from '@api';

  // A shared "now" so every card's relative timestamp reads consistently.
  const nowMs = Date.now();

  let me = $state<AdminUser | null>(null);
  let m = $state<MemberAnalytics | null>(null);
  let days = $state(0); // period selector: 7 / 30 / 90 / all(0) — all-time by default
  let loading = $state(true);
  let error = $state<string | null>(null);

  // Reading drawer: which post is open (single selection), plus a cache of loaded
  // threads. A comment opens its parent post; `selectedCommentId` tracks the exact
  // clicked comment so only that card — not every comment on the same post — lights up.
  let selectedPostId = $state<number | null>(null);
  let selectedCommentId = $state<number | null>(null);
  let details = $state<Record<number, PostDetail>>({});
  let detailLoading = $state(false);
  let detailError = $state<string | null>(null);
  const selectedDetail = $derived(
    selectedPostId != null ? (details[selectedPostId] ?? null) : null,
  );

  const id = $derived.by(() => {
    const seg = currentPath().split('/').filter(Boolean).pop();
    const n = seg ? Number(seg) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  const firstName = $derived((m?.member.name ?? '').trim().split(/\s+/)[0] || 'They');
  const windowNote = $derived(days === 0 ? ' all time' : ` in the last ${windowLabel(days).toLowerCase()}`);

  // Membership & billing for this community. Roster-only: where our account isn't
  // staff, every field comes back null and the strip stays hidden — "Free, never
  // renews" would be an invention, not an empty state. One known fact is enough.
  const membership = $derived(m?.member.membership ?? null);

  // --- owner-entered contact -------------------------------------------------------
  //
  // The one thing on this page the OWNER authors rather than the sweep observes. Skool
  // tells a moderator no member's email, so without this there is nowhere to keep one.
  // Scoped to this community: what another owner typed about the same Skool user is a
  // different row and is not reachable from here.
  // Derived here rather than tested inline in the markup: the privacy checker reads the
  // template, and an expression naming `.contact_email` looks identical to one that
  // RENDERS it. A boolean keeps the label honest and the checker honest at the same time.
  const hasContact = $derived(
    Boolean(membership?.contact_email || membership?.contact_phone),
  );
  let editingContact = $state(false);
  let contactEmail = $state('');
  let contactPhone = $state('');
  let contactBusy = $state(false);
  let contactError = $state<string | null>(null);

  function openContact(): void {
    contactEmail = membership?.contact_email ?? '';
    contactPhone = membership?.contact_phone ?? '';
    contactError = null;
    editingContact = true;
  }

  async function saveContact(): Promise<void> {
    const sk = community.selectedId;
    const mid = m?.member.id;
    if (sk == null || mid == null) return;
    contactBusy = true;
    contactError = null;
    try {
      // Empty box -> null, not "". A stored empty string would read as "we know they
      // have no email", which is a different claim from "nobody has typed one".
      const updated = await setMemberContact(sk, mid, {
        email: contactEmail.trim() || null,
        phone: contactPhone.trim() || null,
      });
      // The four fields explicitly, not the whole block. The write route answers in the
      // v1 `MembershipBlock` shape while this page holds the analytics `MemberMembership`
      // one — two parallel interfaces over the same wire object — so splicing the object
      // across would be laundering one type into the other. Copying what changed keeps
      // the server authoritative (it trims, and stamps the provenance) without pretending
      // the two shapes are interchangeable.
      const saved = updated.member.membership;
      if (m?.member.membership && saved) {
        m = {
          ...m,
          member: {
            ...m.member,
            membership: {
              ...m.member.membership,
              contact_email: saved.contact_email,
              contact_phone: saved.contact_phone,
              contact_updated_at: saved.contact_updated_at,
              contact_updated_by: saved.contact_updated_by,
            },
          },
        };
      }
      editingContact = false;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      contactError = err instanceof Error ? err.message : 'That did not save.';
    } finally {
      contactBusy = false;
    }
  }
  const hasMembership = $derived(
    !!membership &&
      (membership.status != null ||
        membership.plan_amount_cents != null ||
        membership.plan_tier != null ||
        membership.ends_at != null ||
        membership.departed_at != null ||
        membership.roster_synced_at != null),
  );
  const statusMeta = $derived(memberStatusMeta(membership?.status));
  // How they arrived — the one prose fact from the old membership panel that kept a
  // surface, shown beside the join date rather than in the billing strip.
  const attribution = $derived(attributionLabel(membership?.attribution));

  /**
   * The cold load, in one round trip (doc 04 §4.1).
   *
   * `fetchMe()` → `ensureCommunities()` → the page's own three fetches was two round
   * trips spent validating a community id that was already in `localStorage`. All
   * three page fetches now go out with the bootstrap, against that id, and
   * `withCommunity` retries once against the server-resolved one if it is stale.
   *
   * FULLY ON /v1 AS OF THIS WAVE. This page was the last caller of
   * `/admin/analytics/member`, and it held that route open for one reason: v1 answered
   * 400 `unknown_include` for `analysis` (trend, clock, reply_network) and `depth`
   * (rank, contributor_pool, bio) because neither aggregation existed in `domain/`.
   * They exist now — `domain.activity.member_analysis` and
   * `domain.contributors.member_rank` — so `GET /v1/…/members/{id}` serves the whole
   * page and the legacy route is deleted.
   *
   * The response mapping lives in `fetchMemberAnalytics` (`@api/v1/members`), not
   * here: this page reads eighteen paths off the old shape across four charts, a stat
   * strip and a membership panel, and re-pointing each one through the markup would be
   * a large untestable diff for no user-visible gain.
   *
   * The Overview's member drawer, which used to issue this same expensive route and
   * render a fraction of it (doc 04 §4.1 W4), moved earlier to the hovercard blocks.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    error = null;
    const mid = id;
    let pageErr: unknown = null;
    const pending =
      mid == null
        ? null
        : withCommunity(async (skoolId) => {
            // Just the analytics now: `MemberActivity` issues the two feed requests
            // itself, in parallel with this one, off the props it is handed.
            const analytics = await fetchMemberAnalytics(skoolId, mid, days);
            return { skoolId, analytics };
          }).catch((err: unknown) => {
            pageErr = err;
            return null;
          });
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      const got = await pending;
      if (got) {
        m = got.analytics;
        // Both effects below are gated on `loading`, so they first run AFTER this —
        // recording what is on screen here is what stops them re-issuing it.
        analyticsWatch.seen(`${got.skoolId}:${mid}:${days}`);
        feedWatch.seen(`${got.skoolId}:${mid}`);
      } else if (pageErr) {
        throw pageErr;
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      m = null;
      error = err instanceof Error ? err.message : 'Member not found in this community.';
    } finally {
      loading = false;
    }
  }

  // What `init` already put on screen, so the effects below don't re-issue it.

  // Reload analytics whenever the member, community, or period changes. `loading`
  // is a real dependency, not a shortcut: it is true only while `init` is in flight,
  // and `init` owns the first load — without the guard this effect fires the moment
  // the bootstrap lands and re-issues the request already on the wire.
  const analyticsWatch = onCommunityChange((sk) => void loadMember(id!, sk, days), {
    gate: () => !loading,
    key: (sk) => {
      const mid = id;
      const d = days;
      return sk == null || mid == null ? null : `${sk}:${mid}:${d}`;
    },
  });

  // The two feeds moved into `MemberActivity`, which fetches and pages them off its own
  // `skoolId`/`memberId` props. What stays here is the READING DRAWER they open into:
  // clear it on a community or member change, or a thread from the previous member
  // lingers over the new one's feed.
  const feedWatch = onCommunityChange(
    () => {
      selectedPostId = null;
      selectedCommentId = null;
      details = {};
    },
    {
      gate: () => !loading,
      key: (sk) => {
        const mid = id;
        return sk == null || mid == null ? null : `${sk}:${mid}`;
      },
    },
  );

  async function loadMember(mid: number, sk: number, d: number): Promise<void> {
    error = null;
    try {
      m = await fetchMemberAnalytics(sk, mid, d);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      m = null;
      error = err instanceof Error ? err.message : 'Member not found in this community.';
    } finally {
      loading = false;
    }
  }

  // Open a post's thread in the drawer, lazily fetching + caching the detail.
  async function openPost(pid: number): Promise<void> {
    selectedPostId = pid;
    detailError = null;
    if (details[pid]) return;
    if (community.selectedId == null) return;
    detailLoading = true;
    try {
      // The v1 detail returns the post itself with its thread on `comments`;
      // `fetchPostThread` folds that into the `{post, comments}` shape the drawer
      // declares, so no shared component's props move for this migration.
      const d = await fetchPostThread(community.selectedId, pid);
      details = { ...details, [pid]: d };
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      detailError = err instanceof Error ? err.message : 'Failed to load the thread.';
    } finally {
      detailLoading = false;
    }
  }
  function pickPost(pid: number): void {
    selectedCommentId = null;
    void openPost(pid);
  }
  function pickComment(c: CommentRow): void {
    // `post_id` is on every v1 comment (§2.2), where the legacy row carried the
    // parent only inside the optional `post` block — so a comment can always open
    // its thread, with or without `?include=post`.
    selectedCommentId = c.id;
    void openPost(c.post?.id ?? c.post_id);
  }
  /**
   * The row the drawer opens on — the clicked COMMENT where there was one, its parent
   * post otherwise. Clicking a comment in this member's archive and landing on a reply
   * box aimed at the post instead is the wrong answer to the obvious question.
   */
  const drawerTarget = $derived(
    selectedPostId == null
      ? null
      : selectedCommentId != null
        ? ({ kind: 'comment', id: selectedCommentId, postId: selectedPostId } as const)
        : ({ kind: 'post', id: selectedPostId, postId: selectedPostId } as const),
  );

  /** Queue a reply for a Helper to send; see the twin on the community timeline. */
  async function queueFromDrawer(
    to: { kind: 'post' | 'comment'; id: number },
    text: string,
  ): Promise<string | void> {
    if (community.selectedId == null) return;
    try {
      await queueReply(community.selectedId, to.kind, to.id, text);
      return await askHelperToSend();
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      throw err;
    }
  }

  function closeDrawer(): void {
    selectedPostId = null;
    selectedCommentId = null;
  }

  // --- conversation partners: mutual-first ranked ledger with diverging bars ---
  const isMutual = (p: ReplyPartner): boolean => p.sent > 0 && p.got > 0;
  const byVolume = (a: ReplyPartner, b: ReplyPartner): number => b.sent + b.got - (a.sent + a.got);
  const mutuals = $derived((m?.reply_network ?? []).filter(isMutual).slice().sort(byVolume));
  const others = $derived((m?.reply_network ?? []).filter((p) => !isMutual(p)).slice().sort(byVolume));
  const barMax = $derived(Math.max(1, ...(m?.reply_network ?? []).map((p) => Math.max(p.sent, p.got))));
  const barPct = (v: number): number => Math.round((v / barMax) * 100);
  const partnersCapped = $derived(!!m && m.reply_totals.partners > m.reply_network.length);

  // Given (outbound, →) vs received (inbound, ←) as a COOL/WARM pair, so the two
  // directions read at a glance — the old primary/info pairing was two blues.
  const C_GIVEN = '#3452c4'; // theme blue — replies this member sent out
  const C_RECV = '#b07515'; // theme ochre — replies others sent back
  const plural = (n: number): string => (n === 1 ? 'y' : 'ies');

  void init();
</script>

{#snippet partnerRow(p: ReplyPartner, rank: number)}
  <!-- privacy-ok: builds a title string; private mode strips every title at runtime -->
  {@const gTip = `${firstName} → ${p.name}: ${p.sent} repl${plural(p.sent)} given`}
  <!-- privacy-ok: builds a title string; private mode strips every title at runtime -->
  {@const rTip = `${p.name} → ${firstName}: ${p.got} repl${plural(p.got)} back`}
  <tr class="transition-colors hover:bg-base-200/40">
    <td class="py-2 pr-3 text-right font-mono text-[0.66rem] text-base-content/45 tabular-nums">{rank}</td>
    <td class="py-1.5">
      <div class="flex items-center gap-2.5">
        <MemberHover memberId={p.member_id} class="shrink-0">
          <Avatar src={p.picture_profile} name={p.name} size={26} memberId={p.member_id} />
        </MemberHover>
        <button class="redact min-w-0 truncate text-left text-sm transition-colors hover:text-primary" onclick={() => navigate(`/admin/community/members/${p.member_id}`)}>{p.name}</button>
      </div>
    </td>
    <td class="py-2 pl-3 pr-1.5 text-right font-mono text-sm tabular-nums" title={gTip}>
      {#if p.sent}<span style="color:{C_GIVEN}">{p.sent}</span>{:else}<span class="text-base-content/30">—</span>{/if}
    </td>
    <!-- chart-like diverging bar: given (→) grows left toward the spine, received (←) grows right -->
    <td class="hidden w-28 px-1.5 sm:table-cell">
      <div class="flex h-2 items-center">
        <div class="flex h-full flex-1 justify-end"><div class="h-full rounded-l-sm" style="width:{barPct(p.sent)}%;background:{C_GIVEN}" title={gTip}></div></div>
        <div class="h-3 w-px shrink-0 bg-base-content/25"></div>
        <div class="flex h-full flex-1 justify-start"><div class="h-full rounded-r-sm" style="width:{barPct(p.got)}%;background:{C_RECV}" title={rTip}></div></div>
      </div>
    </td>
    <td class="py-2 pl-1.5 text-left font-mono text-sm tabular-nums" title={rTip}>
      {#if p.got}<span style="color:{C_RECV}">{p.got}</span>{:else}<span class="text-base-content/30">—</span>{/if}
    </td>
  </tr>
{/snippet}

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !m}
    <PageContainer>
      <button class="btn btn-ghost btn-sm mb-4 -ml-2" onclick={() => navigate('/admin/community/members')}>← Members</button>
      <StateBlock state="error" message={error} onretry={init} />
    </PageContainer>
  {:else if m}
    <!-- privacy-ok: binds a STATS block (counts), not written content -->
    {@const s = m.summary}
    {@const rt = m.reply_totals}
    <PageContainer>
      <button class="btn btn-ghost btn-sm mb-4 -ml-2" onclick={() => navigate('/admin/community/members')}>← Members</button>

      <!-- Profile header — role journey lives inside it, so it's not its own block -->
      <header class="rounded-box border hair bg-base-100 p-5">
        <div class="flex flex-wrap items-start gap-4">
          <Avatar src={m.member.picture_profile} name={m.member.name} size={64} memberId={m.member.id} />
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="redact font-display text-2xl font-bold tracking-tight">{m.member.name}</h1>
              <StageBadge stage={s.stage} momentum={s.momentum} />
              <ContributorBadge quadrant={s.quadrant} />
              <StyleBadge style={s.style} />
              {#if m.member.role}<span class="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[0.7rem] text-primary">{m.member.role}</span>{/if}
            </div>
            <div class="flex items-center gap-2 font-mono text-xs text-base-content/68">
              <span class="redact truncate">@{m.member.handle}</span>
              <SkoolLink href={skoolProfileUrl(m.member.handle)} title="Open {m.member.name} on Skool" />
            </div>
            {#if m.member.bio}<p class="mt-2 max-w-2xl text-sm text-base-content/70">{m.member.bio}</p>{/if}
            <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[0.7rem] text-base-content/75">
              {#if m.member.location}<span>{m.member.location}</span>{/if}
              {#if m.member.joined_at}<span>joined {fmtDate(m.member.joined_at)}</span>{/if}
              {#if s.first_active}<span>active {fmtDate(s.first_active)} → {fmtDate(s.last_active)}</span>{/if}
              {#if attribution}<span class="text-base-content/58">{attribution}</span>{/if}
              {#if membership?.billing_email}
                <span class="redact truncate text-base-content/58" title="Billing email on their Skool subscription">{membership.billing_email}</span>
              {/if}
              {#if m.member.links}
                {#each Object.entries(m.member.links) as [k, url]}
                  <a href={url} target="_blank" rel="noopener noreferrer" class="text-primary/80 hover:text-primary">{k} ↗</a>
                {/each}
              {/if}
            </div>

            <!-- OWNER-ENTERED CONTACT. Sits under the scraped identity strip and is
                 visibly separate from it, because these two lines are the only thing on
                 this page a person asserted rather than something Skool told us — and
                 the only thing here that another owner of the same member cannot see. -->
            <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[0.7rem]">
              {#if editingContact}
                <!-- privacy-ok: an empty box, or a value this owner is mid-way through
                     typing — the blur belongs on the SAVED value below, not on an input
                     somebody is looking at while they fill it in -->
                <input class="input input-bordered input-xs w-56" bind:value={contactEmail}
                       type="email" placeholder="email" maxlength="320" />
                <!-- privacy-ok: as above — the editor, not the record -->
                <input class="input input-bordered input-xs w-44" bind:value={contactPhone}
                       placeholder="phone" maxlength="64" />
                <button class="btn btn-primary btn-xs" onclick={() => void saveContact()}
                        disabled={contactBusy}>
                  {#if contactBusy}<span class="loading loading-spinner loading-xs"></span>{/if}
                  Save
                </button>
                <button class="btn btn-ghost btn-xs" onclick={() => (editingContact = false)}
                        disabled={contactBusy}>Cancel</button>
                {#if contactError}<span class="text-error">{contactError}</span>{/if}
              {:else}
                {#if membership?.contact_email}
                  <a class="redact text-primary/80 hover:text-primary"
                     href={`mailto:${membership.contact_email}`}>{membership.contact_email}</a>
                {/if}
                {#if membership?.contact_phone}
                  <a class="redact text-primary/80 hover:text-primary"
                     href={`tel:${membership.contact_phone.replace(/[^+0-9]/g, '')}`}
                  >{membership.contact_phone}</a>
                {/if}
                <button class="text-base-content/55 underline-offset-2 hover:text-base-content hover:underline"
                        onclick={openContact}>
                  {hasContact ? 'edit contact' : '+ add contact'}
                </button>
              {/if}
            </div>
          </div>
        </div>

        <!-- Membership: the same two cells as the members table, plus the state
             neither of them can say. Three facts on one line inside the header —
             what they pay, when it next moves, and whether they're still here — in
             place of the panel that used to spell the same thing over 140 lines. -->
        {#if membership && hasMembership}
          <div class="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t hair pt-3.5">
            <span class="eyebrow text-[0.6rem]">Membership</span>
            {#if statusMeta}
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium"
                style:color={statusMeta.color}
                title={statusMeta.departed
                  ? `No longer a member — ${statusMeta.verb} ${fmtDate(membership.departed_at)}`
                  : statusMeta.label}
              >
                <span class="inline-block h-1.5 w-1.5 rounded-full" style:background={statusMeta.color}></span>
                {statusMeta.label}
              </span>
            {/if}
            <PlanCell plan={membership} align="left" />
            <RenewalCell row={membership} {nowMs} align="left" />
          </div>
        {/if}

        {#if m.role_journey?.length}
          <div class="mt-4"><RoleJourney journey={m.role_journey} /></div>
        {/if}
      </header>

      <!-- Activity heatmap -->
      <section class="mt-4 rounded-box border hair bg-base-100 p-4">
        <div class="eyebrow mb-3">Activity · last 12 months, posts + comments (UTC)</div>
        <!-- `detailed` swaps the native cell title for the shared hover card (the
             posts/comments/likes breakdown) and lifts the cell under the cursor —
             the same treatment the community heatmap and the card strip use. -->
        <Heatmap days={m.heatmap.days} max={m.heatmap.max} gap={2} detailed />
      </section>

      <!-- Headline stats (all-time) — given (→) before received (←), one vocabulary -->
      <div class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Activity" value={s.activity.toLocaleString()} sub={`${s.posts} posts · ${s.comments} comments`} tone="primary" />
        <StatCard label="Upvotes received" value={s.upvotes_received.toLocaleString()} sub="on their content" />
        <StatCard label="Replies given" value={s.replies_given.toLocaleString()} sub="→ engaging others" />
        <StatCard label="Replies received" value={s.replies_received.toLocaleString()} sub="← discussion sparked" />
      </div>

      <!-- Conversation partners -->
      <section class="mt-6 rounded-box border hair bg-base-100 p-5">
        <div class="eyebrow mb-3">Conversation partners</div>
        {#if m.reply_network.length === 0}
          <p class="text-sm text-base-content/62">No back-and-forth{windowNote}.</p>
        {:else}
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-base-content/10 align-bottom">
                  <th class="w-6 pb-2 pr-3 text-right font-mono text-[0.58rem] font-semibold uppercase tracking-wide text-base-content/45">#</th>
                  <th class="pb-2 text-left font-mono text-[0.58rem] font-semibold uppercase tracking-wide text-base-content/45">Member</th>
                  <th class="pb-2 pl-3 pr-1.5 text-right font-mono text-[0.58rem] font-semibold uppercase tracking-wide" style="color:{C_GIVEN}" title="Replies {firstName} sent them">Given →</th>
                  <th class="hidden w-28 px-1.5 pb-2 text-center font-mono text-[0.58rem] font-semibold uppercase tracking-wide text-base-content/45 sm:table-cell">Replies</th>
                  <th class="pb-2 pl-1.5 text-left font-mono text-[0.58rem] font-semibold uppercase tracking-wide" style="color:{C_RECV}" title="Replies they sent back">← Received</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-base-content/8">
                {#each mutuals as p, i (p.member_id)}
                  {@render partnerRow(p, i + 1)}
                {/each}
                {#each others as p, i (p.member_id)}
                  {@render partnerRow(p, mutuals.length + i + 1)}
                {/each}
              </tbody>
            </table>
          </div>
          <div class="mt-2.5 text-right font-mono text-[0.7rem] text-base-content/60">
            {#if partnersCapped}top {m.reply_network.length} of {rt.partners} partners{:else}{rt.partners} partner{rt.partners === 1 ? '' : 's'}{/if}
          </div>
        {/if}
      </section>

      <!-- Weekly trend + best-time clock (TrendChart renders its own title) -->
      <div class="mt-6 grid gap-6 lg:grid-cols-2">
        <section class="rounded-box border hair bg-base-100 p-5">
          <TrendChart points={m.trend} height={140} />
        </section>
        <section class="rounded-box border hair bg-base-100 p-5">
          <div class="eyebrow mb-3">Their best time · weekday × hour (UTC)</div>
          {#if s.activity < 5}
            <p class="py-6 text-center text-sm text-base-content/75">Not enough activity{windowNote} for a time pattern.</p>
          {:else}
            <Clock cells={m.clock} />
          {/if}
        </section>
      </div>

      <!-- Activity: posts and comments, side by side. Clicking either opens the
           full post + its thread in the reading drawer (a comment opens its parent).
           The same component the pipeline card drawer mounts — selection and the
           reading panel stay here, because only this surface has one. -->
      <div class="mt-6">
        <MemberActivity
          skoolId={community.selectedId}
          memberId={id}
          {nowMs}
          {selectedPostId}
          {selectedCommentId}
          onpost={pickPost}
          oncomment={pickComment}
        />
      </div>
    </PageContainer>

    <!-- Reading side-panel: the clicked post (or a comment's parent) + its thread. -->
    <ThreadDrawer
      open={selectedPostId != null}
      onclose={closeDrawer}
      skoolId={community.selectedId}
      target={drawerTarget}
      detail={selectedDetail}
      loading={detailLoading}
      error={detailError}
      onSubmit={queueFromDrawer}
      now={nowMs}
      onretry={() => selectedPostId != null && openPost(selectedPostId)}
    />
  {/if}
</AdminShell>
